import { Alert, Platform } from 'react-native';

import { finishAuthNavigation } from '../../core/navigation/navigationRef';
import type { AuthTokens, BudgetUser } from '../../types/auth';
import { toAuthTokens, type AuthResponse } from './services/authService';

export const APPLE_SIGN_IN_BUTTON_HEIGHT = 52;

export const APPLE_LOGIN_FAILED_TITLE = 'Couldn’t sign in with Apple';
export const APPLE_LOGIN_FAILED_BODY =
  'Try again. You can also continue with Google or your phone.';

export function formatAppleFullName(fullName: {
  givenName?: string | null;
  familyName?: string | null;
} | null | undefined): string | undefined {
  if (!fullName) return undefined;
  const parts = [fullName.givenName, fullName.familyName].filter(
    (part): part is string => !!part && part.trim().length > 0,
  );
  const joined = parts.join(' ').trim();
  return joined || undefined;
}

export function isAppleLoginCanceled(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  const record = error as { code?: unknown; error?: unknown; message?: unknown };
  const code = record.code != null ? String(record.code) : '';
  const appleError = typeof record.error === 'string' ? record.error : '';
  const message = typeof record.message === 'string' ? record.message.toLowerCase() : '';
  return (
    code === '1001' ||
    appleError === 'popup_closed_by_user' ||
    appleError === 'user_cancelled_authorize' ||
    message.includes('popup_closed_by_user') ||
    message.includes('user_cancelled_authorize')
  );
}

export function showAppleLoginError(error: unknown): void {
  if (isAppleLoginCanceled(error)) {
    return;
  }
  console.error('[useAppleLogin] ❌', error);
  if (Platform.OS === ('web' as typeof Platform.OS)) {
    // RN Web Alert is easy to miss; surface clearly in the browser.
    // eslint-disable-next-line no-alert
    window.alert(`${APPLE_LOGIN_FAILED_TITLE}\n\n${APPLE_LOGIN_FAILED_BODY}`);
    return;
  }
  Alert.alert(APPLE_LOGIN_FAILED_TITLE, APPLE_LOGIN_FAILED_BODY);
}

export async function completeAppleAuthSession(options: {
  authResponse: AuthResponse;
  login: (tokens: AuthTokens, user: BudgetUser) => Promise<void>;
  exitSampleMode: () => void;
  markPreviewSeen: () => Promise<void>;
}): Promise<void> {
  await options.login(toAuthTokens(options.authResponse), options.authResponse.user);
  options.exitSampleMode();
  await options.markPreviewSeen();
  finishAuthNavigation();
}
